import React, { useMemo, useCallback } from "react";

import {
  Box,
  createColumnHelper,
  DataTable,
  Text,
  type DataTableColumnDef,
  type DataTableGroupType,
  type OnChangeFn,
  type SortingState,
} from "@salestrak/ui";
import { IssueViewContext } from "#app/components/issue-view";
import { useTableViewSettings } from "#app/lib/hooks/use-table-view-settings";
import { DashboardActivityDrilldownDocument, type DashboardDrilldownActivity } from "#app/lib/data";
import {
  GroupQueryProvider,
  useGroupStates,
} from "#app/lib/hooks/group-query-provider";
import { useDrilldownGroups } from "../hooks/use-drilldown-groups";
import {
  DrilldownGroupTitle,
  DrilldownIssueLabel,
  DrilldownMemberTag,
} from "./DrilldownGroupTitle";
import { formatDateShort } from "../lib/utils";
import type { Omittable } from "@salestrak/ui";

const GROUP_PAGE_SIZE = 50;

// ── Row type ─────────────────────────────────────────────────────────────────

type StageChangeRow = {
  id: string;
  _issueId: string;
  project: string;
  date: string;
  member: string;
  from: string;
  to: string;
  address: string;
};

const h = createColumnHelper<StageChangeRow>();

// ── Group title ──────────────────────────────────────────────────────────────

const TargetMetricGroupTitle = React.memo(function TargetMetricGroupTitle({
  userId,
  name,
  count,
  onVisible,
}: Omittable<{
  userId: string;
  name: string;
  count: number;
  onVisible?: () => void;
}>) {
  return (
    <DrilldownGroupTitle onVisible={onVisible}>
      <Box direction="row" gap="xs" align="center">
        <DrilldownMemberTag userId={userId} name={name} />
        <Text size="xs" variant="muted">
          {count} {count === 1 ? "record" : "records"}
        </Text>
      </Box>
    </DrilldownGroupTitle>
  );
});

// ── Props ────────────────────────────────────────────────────────────────────

export type ActivityGroupSummary = {
  userId: string;
  name: string;
  totalChanges: number;
};

export type TargetMetricQueryInput = Omittable<{
  startDate: string;
  endDate: string;
  teamIds?: string[];
  memberIds?: string[];
  sortField?: string;
  sortDirection?: string;
  timezone?: string;
}>;

interface TargetMetricDrilldownProps {
  summaries: ActivityGroupSummary[];
  queryInput: TargetMetricQueryInput;
  sorting: SortingState;
  onSortingChange: OnChangeFn<SortingState>;
}

// ── Component ────────────────────────────────────────────────────────────────

export function TargetMetricDrilldown({
  summaries,
  queryInput,
  sorting,
  onSortingChange,
}: TargetMetricDrilldownProps) {
  const issueViewCtx = React.useContext(IssueViewContext);
  const { registerState, getGroupProps } = useGroupStates<DashboardDrilldownActivity>();
  const summaryKeys = useMemo(() => summaries.map((s) => s.userId), [summaries]);
  const { activatedGroupKeys, activateGroup } = useDrilldownGroups(summaryKeys);

  const openIssue = useCallback(
    (row: StageChangeRow) => {
      if (row._issueId) issueViewCtx?.openIssueById(row._issueId);
    },
    [issueViewCtx],
  );

  const sort = sorting[0];

  const getConnection = useCallback(
    (data: any) => data?.dashboardActivityDrilldown,
    [],
  );

  // The drilldown row is a CLIENT-ONLY aggregate, not a server noun: it
  // collapses and re-sorts the page in memory. It is built here, where the
  // row type is declared, never inside the data layer.
  const toRows = useCallback(
    (nodes: readonly DashboardDrilldownActivity[]) => {
      const rows: StageChangeRow[] = nodes.map((a) => ({
        id: a.id,
        _issueId: a.issueId,
        project: a.issueName ?? `#${a.issueId}`,
        date: formatDateShort(a.creationDate),
        member: a.createdByName ?? "—",
        from: a.oldStageName ?? "—",
        to: a.newStageName ?? "—",
        address: a.issueAddress ?? "—",
      }));

      if (sort) {
        const { id: sortKey, desc } = sort;
        const dir = desc ? -1 : 1;
        rows.sort((a, b) => {
          const aVal = a[sortKey as keyof StageChangeRow] ?? "";
          const bVal = b[sortKey as keyof StageChangeRow] ?? "";
          return String(aVal).localeCompare(String(bVal)) * dir;
        });
      }

      return rows;
    },
    [sort],
  );

  const groups = useMemo<DataTableGroupType<StageChangeRow>[]>(
    () =>
      summaries.map((summary) => ({
        key: summary.userId,
        label: summary.name,
        totalCount: summary.totalChanges,
        ...getGroupProps(summary.userId),
        data: toRows(getGroupProps(summary.userId).data),
      })),
    [summaries, getGroupProps, toRows],
  );

  const groupTitle = useCallback(
    (groupKey: string) => {
      const summary = summaries.find((s) => s.userId === groupKey);
      if (!summary) return null;
      return (
        <TargetMetricGroupTitle
          userId={summary.userId}
          name={summary.name}
          count={summary.totalChanges}
          onVisible={() => activateGroup(groupKey)}
        />
      );
    },
    [activateGroup, summaries],
  );

  const tableView = useTableViewSettings("drilldown:targetMetric");

  const columns = useMemo<DataTableColumnDef<StageChangeRow>[]>(
    () => [
      h.hoverAction({
        accessorKey: "project",
        header: "Project",
        meta: { minWidth: 180 },
        content: (row) => (
          <DrilldownIssueLabel issueId={row._issueId} name={row.project} />
        ),
        onClickAction: openIssue,
        actionLabel: (row) => `Open ${row.project}`,
      }),
      h.accessor({
        accessorKey: "date",
        header: "Date",
        enableSorting: true,
      }),
      h.accessor({
        accessorKey: "from",
        header: "Previous Stage",
        enableSorting: true,
        meta: { minWidth: 200 },
      }),
      h.accessor({
        accessorKey: "to",
        header: "New Stage",
        enableSorting: true,
        meta: { minWidth: 200 },
      }),
      h.accessor({
        accessorKey: "address",
        header: "Address",
        enableSorting: true,
        meta: { minWidth: 250 },
      }),
    ],
    [openIssue],
  );

  return (
    <>
      {summaries
        .filter((summary) => activatedGroupKeys.has(summary.userId))
        .map((summary) => (
          <GroupQueryProvider<DashboardDrilldownActivity>
            key={summary.userId}
            groupKey={summary.userId}
            document={DashboardActivityDrilldownDocument}
            variables={{
              input: {
                ...queryInput,
                subject: "stage",
                targetMetricOnly: true,
                userId: summary.userId,
              },
              limit: GROUP_PAGE_SIZE,
            }}
            getConnection={getConnection}
            onStateChange={registerState}
          />
        ))}

      <DataTable
        columns={columns}
        groups={groups}
        groupTitle={groupTitle}
        getRowId={(r) => r.id}
        density="compact"
        fillColumnId="project"
        pinnedColumnIds={["project"]}
        stickyHeader
        hideGroupCount
        paddingX="md"
        sorting={sorting}
        onSortingChange={onSortingChange}
        {...tableView.tableProps}
      />
    </>
  );
}
